/**
 * Consent state for cookies / analytics.
 *
 * Stored in localStorage under LVS_CONSENT_KEY. Compatible with Google
 * Consent Mode v2 — when the user accepts, we upgrade the consent signals
 * via `gtag('consent', 'update', ...)` before injecting the GTM script.
 *
 * Legal context: Italian Garante della Privacy requires explicit consent
 * before dropping analytics cookies. The default-denied state established
 * in index.html ensures we comply until the user acts.
 */

export const LVS_CONSENT_KEY = "lvs.consent.v1";
export const LVS_CONSENT_EVENT = "lvs:consent-changed";

export type ConsentValue = "granted" | "denied";

export interface ConsentState {
  /** ISO timestamp of the decision */
  ts: string;
  /** Analytics + marketing (GTM, GA4) */
  analytics: ConsentValue;
  /** Marketing / ads personalization */
  marketing: ConsentValue;
  /** Version string — bump to re-prompt all users after policy updates */
  version: string;
}

const GTM_ID = "GTM-W3D227H";
const POLICY_VERSION = "1.0";

export function readConsent(): ConsentState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(LVS_CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentState;
    if (parsed.version !== POLICY_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeConsent(
  partial: Partial<Omit<ConsentState, "ts" | "version">>,
): ConsentState {
  const state: ConsentState = {
    ts: new Date().toISOString(),
    analytics: partial.analytics ?? "denied",
    marketing: partial.marketing ?? "denied",
    version: POLICY_VERSION,
  };
  try {
    window.localStorage.setItem(LVS_CONSENT_KEY, JSON.stringify(state));
  } catch {
    // storage may be disabled — silently continue
  }
  applyConsent(state);
  window.dispatchEvent(new CustomEvent(LVS_CONSENT_EVENT, { detail: state }));
  return state;
}

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    gtag?: (...args: any[]) => void;
    __lvsGtmLoaded?: boolean;
  }
}

/**
 * Apply a consent decision: update Consent Mode v2 signals and, if
 * analytics is granted, inject the GTM script.
 */
export function applyConsent(state: ConsentState): void {
  if (typeof window === "undefined") return;

  window.dataLayer = window.dataLayer || [];
  const gtag =
    window.gtag ||
    function gtagShim() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments as unknown as Record<string, unknown>);
    };

  gtag("consent", "update", {
    analytics_storage: state.analytics,
    ad_storage: state.marketing,
    ad_user_data: state.marketing,
    ad_personalization: state.marketing,
  });

  if (state.analytics === "granted" && !window.__lvsGtmLoaded) {
    injectGtm();
    window.__lvsGtmLoaded = true;
  }
}

/**
 * Inject the GTM loader script. Called only after consent is granted.
 */
function injectGtm(): void {
  const s = document.createElement("script");
  s.async = true;
  s.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
  const first = document.getElementsByTagName("script")[0];
  first?.parentNode?.insertBefore(s, first);
  window.dataLayer?.push({
    "gtm.start": new Date().getTime(),
    event: "gtm.js",
  });
}

/**
 * Rehydrate on page load. If the user previously accepted, re-apply
 * consent so GTM gets injected.
 */
export function rehydrateConsent(): void {
  const state = readConsent();
  if (state) applyConsent(state);
}
